import { useParams, generatePath } from 'react-router-dom';

import { DndContext, useDndContext } from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { PortalDragOverlay } from '@components/drag-and-drop';
import { useSortableDefaults } from '@components/drag-and-drop/defaults';
import { ErrorScreen } from '@components/error-screen';
import { Header } from '@components/header';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconDrag } from '@components/icons/drag';
import { IconDynamicField } from '@components/icons/dynamic-field';
import { IconHelp } from '@components/icons/help';
import { IconInfoSimple } from '@components/icons/info-simple';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconWorkspace } from '@components/icons/workspace';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { Previewer } from '@components/previewer';
import { useIsSidebarActive } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';

import {
  useDocumentTemplate,
  useEditDocumentFields,
  useEditDocumentTemplate,
} from 'api/documents/hooks';
import {
  DocumentTemplate,
  DocumentTemplateDetails,
  DocumentTemplateField,
} from 'api/documents/requests';
import { useLists } from 'api/lists/hooks';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';

import { useDocumentAddEditSidebar } from './add-edit-sidebar';
import { Container, DocumentActions, TYPE_ICON_MAP } from './common';
import { useEditFieldSidebar } from './edit-field-sidebar';
import { usePreviewVariablesModal } from './modals/preview-variables-modal';

export const DocumentDetail = () => {
  const { t } = useScopedTranslate(locale);
  const { documentId } = useParams<{ documentId: string }>();
  const workspace = useCurrentWorkspace();
  const Icon = ICON_CHOOSER_ICONS[workspace.icon] || IconWorkspace;
  const editMutation = useEditDocumentTemplate();

  const handleUpdateName = (name: string) => {
    editMutation.mutateAsync({
      id: documentId ?? '',
      name,
    });
  };

  const { data: document, isError } = useDocumentTemplate(documentId ?? '');

  if (isError) {
    return <ErrorScreen code={404} heading={t('notFound')} message={t('notFoundMessage')} />;
  }
  if (!document) {
    return <Spinner />;
  }

  return (
    <>
      <Header
        breadcrumbs={
          <Breadcrumb
            items={[
              { label: '...' },
              {
                label: t('documents'),
                link: generatePath(routePaths.workspaceDocuments, {
                  workspaceId: document.workspace,
                }),
              },
              {
                label: document.name,
                onRename: handleUpdateName,
              },
            ]}
          />
        }
        actions={<HeaderActions document={document} />}
        backAction={
          <IconContainer
            hasBorder
            icon={<Icon color={workspace.color} />}
            variant={COLOR_TO_VARIANT[workspace.color]}
          />
        }
      />
      <PageContent>
        <Container>
          <DocumentFields document={document} key={(document.dateUpdated ?? '').toString()} />
          <DocumentPreview document={document} />
        </Container>
      </PageContent>
    </>
  );
};

const IconContainer = styled(IconFancyContainer)({
  borderWidth: pxToRem(1),
});

const HeaderActions = ({ document }: { document: DocumentTemplate }) => {
  const { t } = useScopedTranslate(locale);
  const showEditSidebar = useDocumentAddEditSidebar();
  const buttonVariant = 'secondary';
  const indicatorVariant = document.status === 'active' ? 'success' : 'disabled';
  const statusText = document.status === 'active' ? t('active') : t('disabled');

  return (
    <Stack orientation="horizontal" space={4} align="center">
      <TextStatusIndicator variant={indicatorVariant} text={statusText} />
      <Stack orientation="horizontal" space={2}>
        <Button
          text={t('details')}
          iconLeft={<IconInfoSimple />}
          variant={buttonVariant}
          onClick={() => showEditSidebar({ document })}
        />
        <Button
          text={t('learnMore')}
          iconLeft={<IconHelp />}
          variant={buttonVariant}
          onClick={() => showEditSidebar({ document }, { isOverrideEnabled: true })}
        />
        <DocumentActions document={document} variant={buttonVariant} />
      </Stack>
    </Stack>
  );
};

const DocumentFields = ({ document }: { document: DocumentTemplate }) => {
  const { t } = useScopedTranslate(locale);
  const showPreviewModal = usePreviewVariablesModal();
  const isPreviewDisabled = document.fieldConfig.length === 0;

  return (
    <Card space={4} variant="gray" style={{ height: 'fit-content' }}>
      <Stack space={4}>
        <Stack orientation="horizontal" justify="space-between">
          <Stack orientation="horizontal" align="center" space={2}>
            <IconDynamicField color="purple50" />
            <Text
              text={t('variables')}
              font="semibold14"
              style={{ lineHeight: theme.base.height }}
            />
          </Stack>
          <Stack space={2} orientation="horizontal" align="center">
            <Button
              text={t('preview')}
              iconLeft={<IconOpenPreview />}
              disabled={isPreviewDisabled}
              tooltip={isPreviewDisabled ? t('noFields') : undefined}
              variant="secondary"
              onClick={() => showPreviewModal({ document })}
            />
          </Stack>
        </Stack>
        {document.fieldConfig.length === 0 ? (
          <Text text={t('noFields')} />
        ) : (
          <DocumentFieldList document={document} />
        )}
      </Stack>
    </Card>
  );
};

const DocumentFieldList = ({ document }: { document: DocumentTemplate }) => {
  const showEditFieldSidebar = useEditFieldSidebar();
  const listsQuery = useLists();
  const lists = listsQuery.data ?? [];
  const editTemplate = useEditDocumentFields();

  const {
    items,
    itemIds,
    activeItem,
    handleDragEnd,
    handleDragStart,
    handleDragOver,
    strategy,
    ...sortingDefaults
  } = useSortableDefaults({
    items: document.fieldConfig,
    onDragEnd: (newOrder) => {
      const fields = newOrder.map((id) => document.fieldConfig.find((field) => field.id === id)!);
      editTemplate.mutate({ shortId: document.shortId, fieldConfig: fields });
    },
  });

  return (
    <Stack space={2}>
      <DndContext
        {...sortingDefaults}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
      >
        <SortableContext items={itemIds} strategy={strategy}>
          {items.map((field) => (
            <DocumentFieldItem
              key={field.id}
              field={field}
              defaultData={document.defaultData?.[field.id]}
              onClick={() => showEditFieldSidebar({ document, field, lists, id: field.id })}
            />
          ))}
        </SortableContext>

        <PortalDragOverlay dropAnimation={null}>
          {activeItem && (
            <DocumentFieldItem
              field={activeItem}
              defaultData={document.defaultData?.[activeItem.id]}
            />
          )}
        </PortalDragOverlay>
      </DndContext>
    </Stack>
  );
};

const DocumentFieldItem = ({
  field,
  defaultData,
  ...rest
}: {
  field: DocumentTemplateField;
  defaultData?: string;
  onClick?: () => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const Icon = TYPE_ICON_MAP[field.type] ?? TYPE_ICON_MAP.text;
  const { attributes, listeners, setDraggableNodeRef, setDroppableNodeRef, transform, isDragging } =
    useSortable({
      id: field.id,
    });
  const { active: isActiveDrag } = useDndContext();
  const isActive = useIsSidebarActive(field.id);

  const style = {
    transform: CSS.Transform.toString(transform),
    ...(isDragging && {
      opacity: 0,
    }),
  };

  const getFormattedDefaultData = () => {
    if (field.type === 'number') {
      const numericValue = Number(defaultData);
      return Number.isFinite(numericValue) ? formatNumber(numericValue) : String(defaultData ?? '');
    }
    if (field.type === 'date') {
      return abbreviateDate(defaultData as string, 'en');
    }
    return defaultData;
  };

  return (
    <div ref={setDroppableNodeRef} style={{ cursor: 'pointer' }}>
      <div style={style} ref={setDraggableNodeRef}>
        <Card
          interactive
          key={field.id}
          space={2}
          isActive={isActive}
          {...rest}
          {...attributes}
          {...listeners}
        >
          <Stack
            space={2}
            orientation="horizontal"
            align="center"
            style={{ marginLeft: pxToRem(4) }}
          >
            <IconDrag color="purple50" style={{ cursor: isActiveDrag ? 'grabbing' : 'grab' }} />
            <Icon />
            <div style={{ overflow: 'hidden' }}>
              <Text text={field.label} font="semibold14" ellipsis />
              {defaultData ? (
                <Text text={getFormattedDefaultData()} font="regular14" ellipsis />
              ) : (
                <Text text={t('noDefaultValue')} font="regular14" color="purple40" />
              )}
            </div>
          </Stack>
        </Card>
      </div>
    </div>
  );
};

const DocumentPreview = ({ document }: { document: DocumentTemplateDetails }) => {
  const baseUrl = document.previewUrl ?? '';
  const fileLastUpdated = document.dateUpdated
    ? new Date(document.dateUpdated).getTime()
    : Date.now();

  const previewUrl = baseUrl
    ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}date=${fileLastUpdated}`
    : '';

  return (
    <Card space={4} variant="gray" style={{ width: '100%' }}>
      <PDFContainer id="templateFilePreviewer-container">
        <Previewer
          key={previewUrl}
          url={previewUrl}
          maxWidth={1000}
          parentId={'templateFilePreviewer-container'}
          fallback={<></>}
        />
      </PDFContainer>
    </Card>
  );
};

const PDFContainer = styled.div({
  display: 'flex',
  justifyContent: 'center',
});

const locale = {
  en: {
    field: 'Field',
    type: 'Type',
    label: 'Label',
    variables: 'Variables',
    noFields:
      'There are no dynamic fields in this document, to use dynamic fields, the fields you want to use have to be denoted with {{curly}} brackets in the document.',
    download: 'Download',
    replace: 'Replace',
    testValuePreview: 'Test value for preview',
    document: 'Document',
    documents: 'Documents',
    preview: 'Preview',
    text: 'Text short',
    paragraph: 'Text long',
    number: 'Number',
    email: 'Email',
    phone: 'Mobile number',
    date: 'Date',
    select: 'Select',
    notFound: 'Not found',
    notFoundMessage: 'Document template not found. Are you sure it exists?',
    noDefaultValue: 'No default value',
    learnMore: 'Learn more',
    details: 'Details',
    active: 'Active',
    disabled: 'Disabled',
  },
};
